import { Button, Group, Select, Stack } from "@mantine/core";
import { Form, useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { zodResolver } from "mantine-form-zod-resolver";
import { GeoFilter, GeoLayer } from "shared/components/organization/geolocations/types";
import { GEO_QUERY_KEY, muts } from "shared/components/organization/geolocations/useGeoLocations";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { queryClient } from "src/queryClient";
import { z } from "zod";

const schema = z.object({
    targetLayerId: z.string().min(1, "Destination layer is required"),
});

type MoveShapeFormProps = {
    shape: GeoFilter;
    availableLayers: GeoLayer[];
    onCancel: () => void;
};

export const MoveShapeForm = ({ shape, availableLayers, onCancel }: MoveShapeFormProps) => {
    const updateShape = useGraphqlMutation(muts.updateShape);

    const form = useForm({
        initialValues: {
            targetLayerId: availableLayers[0]?.id ?? "",
        },
        validate: zodResolver(schema),
    });

    function handleSubmit(values: typeof form.values) {
        if (!values.targetLayerId) return;
        updateShape
            .mutateAsync({ id: shape.id, name: shape.name, boundary: shape.boundary, layerId: values.targetLayerId })
            .then(() => {
                notify.show.success("Shape moved");
                queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
                    old
                        ? old.map((layer) => {
                              if (layer.id === values.targetLayerId) {
                                  return layer.filters ? { ...layer, filters: [...layer.filters, shape] } : layer;
                              }
                              return layer.filters ? { ...layer, filters: layer.filters.filter((f) => f.id !== shape.id) } : layer;
                          })
                        : old,
                );
                modals.closeAll();
            })
            .catch(notify.catch);
    }

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Stack>
                <Select
                    label="Destination Layer"
                    data={availableLayers.map((layer) => ({ value: layer.id, label: layer.name }))}
                    {...form.getInputProps("targetLayerId")}
                    required
                />
                <Group justify="flex-end">
                    <Button variant="default" onClick={onCancel} disabled={updateShape.isPending}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={updateShape.isPending}>
                        Move
                    </Button>
                </Group>
            </Stack>
        </Form>
    );
};
